import "../../../styles/global.scss";
import React, { useEffect, useMemo, useState } from "react";
import DashboardLayout from "./DashboardLayout";
import PlacementsContent from "../../../components/PlacementsContent";
import Map from "../../../components/map";
import useSWR from "swr";
import { serverUrl, fetcher, swrConfig } from "../../../helpers/utility";
import { Row, Col, Container, Card, Badge } from "react-bootstrap";
import Loading from "../../../components/loading";
import InsightsTab from "../../../components/InsightsTab";
import { ArrowRightIcon } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import axios from 'axios';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
dayjs.extend(advancedFormat);

const S3_BUCKET_URL = "https://bplacedprivate.s3.eu-west-2.amazonaws.com";

function Dashboard({ user, isGoogleMapLoaded }) {
  const [insightsData, setInsightsData] = useState(null);
  const [areaName, setAreaName] = useState('');
  const [mapCenter, setMapCenter] = useState({ lat: 0, lng: 0 });
  const navigate = useNavigate();


  const { data: localAuthorityData, isLoading: localAuthorityIsLoading, error: localAuthorityError } = useSWR(
    `${serverUrl}/local-authority`,
    fetcher,
    swrConfig
  );

  useEffect(() => {
    if (localAuthorityData?.localAuthority) {
      setAreaName(localAuthorityData?.localAuthority?.name);
      setMapCenter({ lat: localAuthorityData.localAuthority.lat, lng: localAuthorityData.localAuthority.lng });
    }
  }, [localAuthorityData]);

  const { data: locationData, isLoading: locationIsLoading } = useSWR(
    localAuthorityData?.localAuthority?.lat && 
    localAuthorityData?.localAuthority?.lng && 
    areaName
      ? `${serverUrl}/location/area/${areaName}?lat=${localAuthorityData.localAuthority.lat}&lng=${localAuthorityData.localAuthority.lng}`
      : null,
    fetcher,
    swrConfig
  );

  // Fetch services from the database
  const { data: serviceData, isLoading: serviceIsLoading } = useSWR(
    `${serverUrl}/service`,
    fetcher,
    swrConfig
  );

  // Get the first 5 services for the dashboard summary
  const servicesPreview = useMemo(() => {
    if (!serviceData?.services) return [];
    return serviceData.services.slice(0, 5);
  }, [serviceData]);

  const locationsParsed = useMemo(() => {
    if (!locationData?.locations || locationData.locations.length === 0) {
      return [];
    }

    // Filter locations that have valid coordinates
    return locationData.locations
      .filter((location) => location?.address?.lat && location?.address?.lng)
      .map((locationItem) => ({
        ...locationItem,
        position: {
          lat: locationItem.address.lat,
          lng: locationItem.address.lng,
        },
        key: `${locationItem._id}`,
      }));
  }, [locationData]);


    //   {
    //     id: 1,
    //     name: "Sunshine Care Services",
    //     rating: 4.8,
    //     specializations: ["Foster Care", "Residential"],
    //   },
    //   {
    //     id: 2,
    //     name: "Family First Care",
    //     rating: 4.6,
    //     specializations: ["Emergency Placements", "Short-term"],
    //   },
    //   {
    //     id: 3,
    //     name: "Hope & Healing Homes",
    //     rating: 4.9,
    //     specializations: ["Therapeutic Care", "Long-term"],
    //   },
    //   {
    //     id: 4,
    //     name: "Safe Haven Care",
    //     rating: 4.7,
    //     specializations: ["Sibling Groups", "Respite"],
    //   },
    //   {
    //     id: 5,
    //     name: "Bright Futures Care",
    //     rating: 4.5,
    //     specializations: ["Teenage Care", "Supported Living"],
    //   },
    //   {
    //     id: 6,
    //     name: "Compassionate Care Network",
    //     rating: 4.8,
    //     specializations: ["Complex Needs", "Foster Care"],
    //   },
    // ];

  // Calculate center point for the map (area-specific default, or center of all locations)
  // const mapCenter = useMemo(() => {
  //   if (locationsParsed.length === 0) {
  //     // Area-specific default centers
  //     // const areaCenters = {
  //     //   'bristol': { lat: 51.50, lng: -2.60 },
  //     //   'london': { lat: 51.5074, lng: -0.1278 },
  //     //   'manchester': { lat: 53.4808, lng: -2.2426 },
  //     //   'birmingham': { lat: 52.4862, lng: -1.8904 },
  //     //   'leeds': { lat: 53.8008, lng: -1.5491 }
  //     // };
  //     return (
  //       setMapCenter({ lat: localAuthorityData.localAuthority.lat, lng: localAuthorityData.localAuthority.lng })
  //     );
  //   }

  //   // // Calculate average center of all locations
  //   // const avgLat =
  //   //   locationsParsed.reduce((sum, loc) => sum + loc.position.lat, 0) /
  //   //   locationsParsed.length;
  //   // const avgLng =
  //   //   locationsParsed.reduce((sum, loc) => sum + loc.position.lng, 0) /
  //   //   locationsParsed.length;

  //   // return { lat: avgLat, lng: avgLng };
  // }, [locationsParsed, areaName]);

  async function fetchInsightsData() {
    try {
      const response = await axios.get(`${serverUrl}/insight/${user.localAuthorityId}/current`);
      if (response.data.insight) {
        setInsightsData(response.data.insight);
        return response.data.insight;
      } else {
        setInsightsData(null);
        return null;
      }
    } catch (error) {
      console.error("Error getting insights data:", error);
      // alert("Failed to get insights data. Please try again.");
    }
  }

  useEffect(() => {
    fetchInsightsData();
  }, []);

  const onMarkerClicked = (location) => {
    navigate(`/location?locationId=${location._id}`);
  };

  return (
    <DashboardLayout user={user}>
      <Container fluid>
        <Row className="h-100" style={{ marginTop: 0 }}>
          <Col lg={7} style={{ height: "100%", overflow: "hidden" }}>
            <PlacementsContent summaryOnly={true} />
          </Col>
          <Col lg={5} style={{ height: "100%", overflow: "hidden" }}>
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <div className="d-flex flex-row mb-2">
                <h1 className="h3">Care Providers</h1>
                <p className="text-muted medium ms-auto">
                  Your location: {areaName.charAt(0).toUpperCase() + areaName.slice(1)}
                </p>
              </div>
              <Row>
                <Col lg={12}>
                  {locationIsLoading ? (
                    <Loading inline={true} inlineHeight={400} />
                  ) : (
                    <div style={{ height: "265px", width: "100%" }}>
                      {isGoogleMapLoaded && (
                        <Map
                          isLoaded={isGoogleMapLoaded}
                          center={mapCenter}
                          locations={locationsParsed}
                          educationAddress={undefined}
                          onClick={onMarkerClicked}
                          heightOverride="500px"
                          disableDefaultUI={true}
                        />
                      )}
                      {!isGoogleMapLoaded && (
                        <div className="d-flex align-items-center justify-content-center h-100">
                          <p>Loading map...</p>
                        </div>
                      )}
                    </div>
                  )}
                </Col>
              </Row>
            </div>
          </Col>
        </Row>
        <Row className="mt-4">
          {insightsData && <Col lg={7}>
            <div className="d-flex flex-column bg-white p-4 rounded-lg shadow-sm">
              <div className="d-flex flex-row mb-2">
                <h1 className="h3">Insights</h1>
                <p className="text-muted medium ms-auto">
                  Last updated:{" "}
                  {insightsData?.updatedAt
                    ? dayjs(insightsData.updatedAt).format("MMM Do YYYY")
                    : "N/A"}
                </p>
              </div>
              <InsightsTab summaryOnly={true} insightsData={insightsData} />
              <Link className="ms-auto text-primary" to="/local-authority/dashboard/insights">
                See all Insights <ArrowRightIcon />
              </Link>
            </div>
          </Col>}
          <Col lg={5}>
            <div className="d-flex flex-column bg-white p-4 rounded-lg shadow-sm">
              <div className="d-flex flex-row mb-2">
                <h1 className="h3">Services</h1>
                <p className="text-muted medium ms-auto">
                  {servicesPreview.length} of {serviceData?.services?.length || 0} services
                </p>
              </div>
              <div className="flex-grow-1 overflow-auto" style={{ height: "190px" }}>
                {serviceIsLoading ? (
                  <Loading inline={true} inlineHeight={150} />
                ) : servicesPreview.length === 0 ? (
                  <div className="d-flex align-items-center justify-content-center h-100">
                    <p className="text-muted">No services available</p>
                  </div>
                ) : (
                  servicesPreview.map((service) => (
                    <Card key={service._id} className="mb-3 shadow-sm">
                      <Card.Body className="p-3 d-flex">
                        {service.imageUrl?.externalId && (
                          <div
                            style={{
                              width: 50,
                              height: 50,
                              borderRadius: 6,
                              overflow: "hidden",
                              marginRight: 12,
                              flexShrink: 0,
                            }}
                          >
                            <img
                              src={`${S3_BUCKET_URL}/${service.imageUrl.externalId}`}
                              alt={service.businessName}
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                              }}
                            />
                          </div>
                        )}
                        <div className="flex-grow-1">
                          <Card.Title className="h6 text-primary mb-2">{service.businessName}</Card.Title>
                          <div className="d-flex flex-wrap gap-1">
                            {service.serviceType && (
                              <Badge bg="primary" style={{ fontSize: "0.7rem" }}>
                                {service.serviceType}
                              </Badge>
                            )}
                            {service.deliveryMethod && (
                              <Badge bg="secondary" style={{ fontSize: "0.7rem" }}>
                                {service.deliveryMethod === "in-person"
                                  ? "In-Person"
                                  : service.deliveryMethod === "online"
                                  ? "Online"
                                  : "Both"}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </Card.Body>
                    </Card>
                  ))
                )}
              </div>
              <Link className="ms-auto text-primary pt-3" to="/local-authority/dashboard/services">
                See all Services <ArrowRightIcon />
              </Link>
            </div>
          </Col>
        </Row>
      </Container>
    </DashboardLayout>
  );
}

export default Dashboard;
